import React, { ReactNode, useMemo } from "react";
import { Accordion, Title, Text, Stack, Card, Divider, List, ListItem } from "@bookingcom/bui-react";
import { useI18n, Trans } from "@bookingcom/lingojs-react";
import { t } from "@bookingcom/lingojs-core";
import { trackCheckinPage } from "../../../utils/experiments/post-booking/flights_web_pb_checkin_page_v2_m2";

type AnswerContent = {
  type: "text" | "ol";
  content: ReactNode[];
};

type FAQContent = {
  header: ReactNode;
  answer?: AnswerContent[];
  onOpen?: () => void;
};

export const CheckinFAQs: React.FC = () => {
  const i18n = useI18n();

  const faqsContent: FAQContent[] = useMemo(
    () => [
      // When can I check in?
      {
        header: <Trans tag="flights_pb_checkin_tips_q_when" />,
        answer: [
          {
            type: "text",
            content: [
              <Trans tag="flights_pb_checkin_tips_a_when_1" key={0} />,
              <Trans tag="flights_pb_checkin_tips_a_when_2" key={1} />
            ]
          }
        ],
        onOpen: () => trackCheckinPage.goals.CLICK_WHENCHECKIN()
      },
      // How do I check in online?
      {
        header: <Trans tag="flights_pb_checkin_tips_q_how" />,
        answer: [
          {
            type: "ol",
            content: [
              <Trans
                tag="flights_pb_checkin_tips_a_how_1"
                components={[<strong key={0} />]}
                variables={{ start_bold: "<0>", end_bold: "</0>" }}
                key={0}
              />,
              <Trans
                tag="flights_pb_checkin_tips_a_how_2"
                components={[<strong key={0} />]}
                variables={{ start_bold: "<0>", end_bold: "</0>" }}
                key={1}
              />,
              <Trans
                tag="flights_pb_checkin_tips_a_how_3"
                components={[<strong key={0} />]}
                variables={{ start_bold: "<0>", end_bold: "</0>" }}
                key={2}
              />
            ]
          }
        ],
        onOpen: () => trackCheckinPage.goals.CLICK_HOWCHECKIN()
      }
    ],
    []
  );

  const questionsCount = faqsContent.length;

  const listContent = (content: ReactNode[]) => {
    return content.map((contentItem: ReactNode, index) => <ListItem key={index}>{contentItem}</ListItem>);
  };

  return (
    <Stack attributes={{ style: { marginBottom: "var(--bui_spacing_4x)" } }}>
      <Title variant="headline_3" title={i18n.trans(t("flights_pb_checkin_tips_title"))} />
      <Card>
        <Stack gap={4}>
          {faqsContent.map((faq: FAQContent, index: number) => {
            const question = <Title variant="strong_1" title={faq.header} />;
            const answer = faq.answer?.map(({ type, content }: AnswerContent) => {
              switch (type) {
                case "text":
                  return (
                    <Stack>
                      {content.map((contentItem: ReactNode, index) => (
                        <Text key={index} variant="body_2">
                          {contentItem}
                        </Text>
                      ))}
                    </Stack>
                  );
                case "ol":
                  return <List variant="ordered">{listContent(content)}</List>;
                default:
                  return null;
              }
            });
            return (
              <React.Fragment key={index}>
                <Accordion titleContent={question} onOpen={faq.onOpen}>
                  {answer}
                </Accordion>
                {index !== questionsCount - 1 && <Divider />}
              </React.Fragment>
            );
          })}
        </Stack>
      </Card>
    </Stack>
  );
};
